import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { Quat } from '../../engine/math/quat';
import { Mat4 } from '../../engine/math/mat4';
import { Rng } from '../../engine/math/random';
import { MeshData, Color, translate, scale as scaleMesh, setEmissive } from '../../engine/geo/mesh';
import { cylinder, sphere, cone, box, combine } from '../../engine/geo/primitives';
import { GpuMesh, INSTANCE_STRIDE } from '../../engine/render/GpuMesh';
import type { Ctx, Renderable } from '../Ctx';

export interface Instance { pos: Vec3; yaw?: number; scale?: number | Vec3; tint?: Color; emissive?: number; tilt?: number }

/** Upload a list of instances of one mesh as a single instanced draw. */
export function instanced(ctx: Ctx, name: string, mesh: MeshData, list: Instance[], opts: Partial<Renderable> = {}): { gm: GpuMesh; rd: Renderable; buf: Float32Array; count: number } {
  const gm = ctx.renderer.createMesh(mesh, name);
  const buf = new Float32Array(list.length * INSTANCE_STRIDE);
  const m = new Mat4(), q = new Quat(), s = new Vec3();
  list.forEach((inst, i) => {
    const sc = inst.scale ?? 1;
    if (typeof sc === 'number') s.set(sc, sc, sc); else s.copy(sc);
    q.setEuler(inst.yaw ?? 0, inst.tilt ?? 0, 0);
    m.compose(inst.pos, q, s);
    const t = inst.tint ?? [1, 1, 1];
    GpuMesh.writeInstance(buf, i, m.e, t[0], t[1], t[2], inst.emissive ?? 0);
  });
  gm.setInstances(buf);
  const e = ctx.spawn(name, new Transform(), gm, { instanced: true, ...opts });
  return { gm, rd: ctx.renderables.req(e), buf, count: list.length };
}

/** §10: every prop gets ±6% random scale and a random yaw. */
export function jitterInstance(rng: Rng, pos: Vec3, spread = 0.06): Instance {
  return { pos, yaw: rng.range(0, Math.PI * 2), scale: rng.jitter(spread), tilt: rng.range(-0.04, 0.04) };
}

/** A tree is a tapered trunk with three stacked, offset, flattened spheres. Base at y=0. */
export function treeMesh(trunkColor: Color, leafColor: Color, h = 7, r = 0.45, canopy = 2.6, seed = 1): MeshData {
  const rng = new Rng(seed);
  const trunk = translate(cylinder(r * 0.6, r, h, 7, trunkColor), 0, h / 2, 0);
  const balls: MeshData[] = [];
  for (let i = 0; i < 3; i++) {
    const s = sphere(canopy * (1 - i * 0.18), 8, 5, leafColor);
    scaleMesh(s, 1, 0.72, 1);
    translate(s, rng.range(-0.8, 0.8), h - 0.6 + i * canopy * 0.55, rng.range(-0.8, 0.8));
    balls.push(s);
  }
  return combine(trunk, ...balls);
}

/** Dark, near-total canopy disc on a tall trunk (Ironwood). */
export function ironwoodTreeMesh(trunkColor: Color, discColor: Color, h = 12, r = 0.8): MeshData {
  const trunk = translate(cylinder(r * 0.7, r * 1.15, h, 7, trunkColor), 0, h / 2, 0);
  const disc = translate(cylinder(4.5, 4.8, 0.5, 9, discColor), 0, h, 0);
  const disc2 = translate(cylinder(3.2, 3.4, 0.4, 8, discColor), 1.2, h - 1.6, 0.8);
  return combine(trunk, disc, disc2);
}

/** Rounded hedgerow block. Base at y=0. */
export function hedgeMesh(color: Color, w = 4, h = 1.6, d = 1.4): MeshData {
  const b = box(w, h, d, color);
  translate(b, 0, h / 2, 0);
  const caps = combine(
    translate(sphere(d / 2, 8, 5, color), -w / 2, h / 2, 0),
    translate(sphere(d / 2, 8, 5, color), w / 2, h / 2, 0),
    translate(scaleMesh(sphere(1, 8, 5, color), w / 2, h * 0.35, d / 2 + 0.05), 0, h * 0.9, 0),
  );
  return combine(b, caps);
}

/** Grass tuft: three small cones. Base at y=0. Tagged for wind by the caller. */
export function grassMesh(color: Color): MeshData {
  return combine(
    translate(cone(0.12, 0.6, 4, color), 0, 0.3, 0),
    translate(cone(0.1, 0.45, 4, color), 0.14, 0.22, 0.05),
    translate(cone(0.1, 0.5, 4, color), -0.12, 0.25, -0.06),
  );
}

/** Reed: tall thin cylinder with an emissive tip sphere (marsh). */
export function reedMesh(stalk: Color, tip: Color): MeshData {
  const s = translate(cylinder(0.02, 0.04, 2.2, 5, stalk), 0, 1.1, 0);
  const t = setEmissive(translate(sphere(0.07, 6, 4, tip), 0, 2.25, 0), tip, 1.0);
  return combine(s, t);
}

/** Standing stone: tapered octagonal cylinder. Base at y=0. */
export function standingStoneMesh(color: Color, h = 2.4, r = 0.55): MeshData {
  return translate(cylinder(r * 0.7, r, h, 7, color), 0, h / 2, 0);
}

/** Boulder: noise-free lumpy sphere (three merged spheres). Base ~y=0. */
export function boulderMesh(color: Color, r = 1): MeshData {
  return combine(
    translate(scaleMesh(sphere(r, 8, 5, color), 1, 0.7, 1), 0, r * 0.55, 0),
    translate(scaleMesh(sphere(r * 0.7, 7, 5, color), 1, 0.8, 1), r * 0.6, r * 0.5, r * 0.3),
    translate(scaleMesh(sphere(r * 0.6, 7, 5, color), 1, 0.7, 1), -r * 0.5, r * 0.45, -r * 0.4),
  );
}

/** Mushroom: stem + cap. Base at y=0. */
export function mushroomMesh(stem: Color, cap: Color, h = 0.6, r = 0.35): MeshData {
  return combine(
    translate(cylinder(0.08, 0.1, h, 6, stem), 0, h / 2, 0),
    translate(scaleMesh(sphere(r, 8, 4, cap), 1, 0.55, 1), 0, h, 0),
  );
}
